// Modules
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import React from 'react';
import _ from 'lodash';
import classNames from 'classnames';
import moment from 'moment';

import { Page, PageHeader, ActionSection, PageContent } from 'components/page';
import { didQueryUpdate } from 'utils/filter-utils';
import { getBadgeType } from 'utils/badge-utils';
import { isMobile } from 'constants';
import { mergeQuery } from 'modules/history/actions';
import { mergeSocketArray } from 'utils/socket-utils';
import FilterForm from 'modules/filters/filters';
import MenuAlert from 'components/main-menu/components/menu-alert';
import Ticket from 'services/data/ticket';
import TicketGroup from 'components/models/ticket/ticket-group';
import * as socket from 'services/socket';

import * as actions from './store/actions';
import * as utils from './utils/load-tickets';

function getColumns(columns) {
  const needed = _(columns).toArray().filter('length').value().length;
  const { high, medium, low } = columns;
  const output = { high: 1, medium: 1, low: 1 };
  let available = 3 - needed;

  // The default situation
  if (!available || !needed || isMobile) return output;

  if (high && high.length >= 5) {
    output.high = 1 + available;
    available = 0;
  }

  if (medium && medium.length >= 5) {
    output.medium = 1 + available;
    available = 0;
  }

  if (low && low.length >= 5) {
    output.low = 1 + available;
    available = 0;
  }

  return output;
}

class ViewTickets extends React.Component {
  static propTypes = {
    query: PropTypes.object,
    tickets: PropTypes.array,
    fetching: PropTypes.bool,
    hideLowPriority: PropTypes.bool,
    currentUser: PropTypes.object.isRequired,
    users: PropTypes.array
  };

  constructor() {
    super();
    this.search = this.search.bind(this);
    this.handleSocketUpdate = this.handleSocketUpdate.bind(this);
    this.mergeSocketTickets = this.mergeSocketTickets.bind(this);
    this.setAssignedTo = this.setAssignedTo.bind(this);
    this.setDate = this.setDate.bind(this);

    this.filterConfig = {
      basic: ['company'],
      advanced: [
        ['search', 'company', 'team'],
        ['dueDate', 'taskRecurrence', 'assignedTo'],
        ['ticketType', 'ticketStatus']
      ],
      defaults: () => ({
        ticketStatus: ['Active'],
        assignedTo: this.setAssignedTo('me', false),
        ticketType: ['Help Desk', 'Project', 'Maintenance', 'Business Development', 'Internal', 'Resync', 'Praece Protect'],
        taskRecurrence: ['Recurring tasks', 'Non-recurring tasks'],
        dueDate: this.setDate(0, false)
      }),
      autoFilters: [
        [
          { label: 'Today', fn: () => this.setDate(0), active: filters => _.isEqual(filters.dueDate, this.setDate(0, false)) },
          { label: '+1D', fn: () => this.setDate(1), active: filters => _.isEqual(filters.dueDate, this.setDate(1, false)) },
          { label: '+7D', fn: () => this.setDate(7), active: filters => _.isEqual(filters.dueDate, this.setDate(7, false)) },
          { label: 'All', fn: () => mergeQuery({ where: { dueDate: { max: null, min: null } } }, true), active: filters => !filters.dueDate || (!filters.dueDate.max && !filters.dueDate.min) }
        ],
        [
          { label: 'Me', fn: () => this.setAssignedTo('me'), active: filters => _.isEqual(filters.assignedTo, this.setAssignedTo('me', false)) },
          { label: 'Team', fn: () => this.setAssignedTo('team'), active: filters => _.isEqual(filters.assignedTo, this.setAssignedTo('team', false)) },
          { label: 'All', fn: () => this.setAssignedTo('all'), active: filters => _.isEqual(filters.assignedTo, this.setAssignedTo('all', false)) }
        ]
      ]
    };
  }

  componentWillMount() {
    this.deregisterSocket = socket.registerSocket('ticket', this.handleSocketUpdate, this.handleSocketUpdate);
  }

  componentWillReceiveProps(nextProps) {
    if (didQueryUpdate(nextProps, this.props)) this.search(nextProps.query);
  }

  componentWillUnmount() {
    this.deregisterSocket('ticket');
    utils.cancelLoadTickets();
    actions.clearPageData();
  }

  setAssignedTo(to, set = true) {
    const { query, currentUser, users } = this.props;
    const where = _.cloneDeep(query.where) || {};
    const teams = _.groupBy(users, 'team.id');
    const teamId = _.get(currentUser, 'team.id', currentUser.team);

    if (to === 'me') where.assignedTo = [currentUser.id];
    if (to === 'team') where.assignedTo = _.map(teams[teamId], 'id');
    if (to === 'all') where.assignedTo = undefined;

    if (set) mergeQuery({ where });

    return where.assignedTo ? _.map(where.assignedTo, _.toString) : where.assignedTo;
  }

  setDate(n, set = true) {
    const dueDate = { max: moment().endOf('day').add(n, 'days').toISOString(), min: '' };

    if (set) mergeQuery({ where: { dueDate } }, true);

    return dueDate;
  }

  handleSocketUpdate() {
    utils.debouncedLoadTickets(this.props.query, { merge: this.mergeSocketTickets });
  }

  mergeSocketTickets(loadedTickets) {
    const nestedMerge = ['tasks.checklist'];
    const mergeIfLocked = ['openTaskCount'];

    return mergeSocketArray(loadedTickets, this.props.tickets, {
      nestedMerge,
      mergeIfLocked,
      keepItemsFilters: ['locked', { status: 'Active' }]
    });
  }

  search(query) {
    utils.loadTickets(query || this.props.query, { fetching: true });
  }

  render() {
    const { fetching, tickets: ticketArray, hideLowPriority, query } = this.props;
    const tickets = Ticket.sort(_.compact(ticketArray), true);

    const groups = _.groupBy(tickets, Ticket.getUrgency);
    const { high, medium, low } = groups;
    const { high: highColumns, medium: mediumColumns, low: lowColumns } = getColumns(groups);

    const taskIsOpen = _(tickets).map('tasks').flatten().some('open');
    const ticketIsOpen = _.some(tickets, 'addingTask') || _.some(tickets, 'addingTimeReport');

    const maxDate = _.get(query, 'where.dueDate.max');
    const dateCutoff = moment(maxDate).endOf('day');
    const tasks = _(tickets)
      .map('tasks')
      .flatten()
      .filter(task => {
        if (!maxDate) return true;

        return moment(task.dueDate).isSameOrBefore(dateCutoff);
      })
      .value();

    const byPriority = _.groupBy(tasks, 'priority');
    const overdue = _.filter(tasks, task => moment(task.dueDate).isBefore(moment().startOf('day')));
    const counts = {
      critical: _.get(byPriority, 'Critical.length', 0),
      urgent: _.get(byPriority, 'Urgent.length', 0),
      overdue: overdue.length,
      total: tasks.length
    };

    const badge = getBadgeType(counts);
    const menuAlert = counts.total ? <MenuAlert className={`badge-${badge}`}>{counts.total}</MenuAlert> : null;

    return (
      <Page className="tickets-page">
        <PageHeader title="Tickets">
          <ActionSection>
            <FilterForm menuAlert={menuAlert} config={this.filterConfig} />
          </ActionSection>
        </PageHeader>
        <PageContent
          fetching={fetching}
          emptyText="No tickets, adjust your filters!"
          emptyTextIcon="svg/ticket.svg"
          showEmptyText={!fetching && _.isEmpty(tickets)}
          className="tickets-content bg-medium-blue-grey"
        >
          <div className={classNames('tickets', { open: taskIsOpen || ticketIsOpen, 'hide-low-priority': hideLowPriority })}>
            <TicketGroup
              tickets={high}
              columns={highColumns}
              updateTicket={actions.updateTicket}
              updateTask={actions.updateTask}
            />
            <TicketGroup
              tickets={medium}
              columns={mediumColumns}
              updateTicket={actions.updateTicket}
              updateTask={actions.updateTask}
            />
            <TicketGroup
              tickets={low}
              columns={lowColumns}
              updateTicket={actions.updateTicket}
              updateTask={actions.updateTask}
            />
          </div>
        </PageContent>
      </Page>
    );
  }
}

// Exports
export default connect((state) => ({
  query: state.location.query,
  teams: state.pageData.teams,
  users: state.pageData.selectUsers,
  tickets: state.tickets.tickets,
  fetching: state.tickets.fetching,
  currentUser: state.auth.currentUser,
  hideLowPriority: state.tickets.hideLowPriority
}))(ViewTickets);
